import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, message, Modal, Select, Tag, Upload } from "antd";
import React, { useEffect, useState } from "react";
import { MoneyInput } from "~/components/form/MoneyInput.jsx";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";

const MAX_FILE_SIZE = 4 * 1000 * 1000;

/**
 * Create a declaration, or change one that is still pending (editing = ledger entry with declaration).
 */
export function DeclarationModal({ open, onClose, onSaved, products, editing = null }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);

  const existingFilename = editing?.declaration?.filename || "";
  const hasExistingFile = !!editing?.declaration?.file_url && !removeFile;
  const productId = Form.useWatch("product_id", form);
  const product = products.find((p) => p.id === productId);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setRemoveFile(false);
    form.resetFields();
    if (editing) {
      form.setFieldsValue({
        product_id: editing.product_id,
        amount: editing.amount_calculated,
        remark: editing.remark,
      });
    } else if (products.length === 1) {
      form.setFieldsValue({ product_id: products[0].id });
    }
  }, [open, editing]);

  const selectFile = (selected) => {
    if (selected.size > MAX_FILE_SIZE) {
      message.error($t("Je mag maximaal ${size} uploaden.", { size: "4MB" }));
      return false;
    }
    setFile(selected);
    setRemoveFile(false);
    return false;
  };

  const save = async () => {
    const values = await form.validateFields();

    if (product?.declaration_attachment_mandatory && !file && !hasExistingFile) {
      message.error($t("Voor deze categorie is een bijlage verplicht."));
      return;
    }

    const payload = {
      product_id: values.product_id,
      amount: values.amount,
      remark: values.remark || "",
    };
    if (file) payload.file = file;
    if (removeFile && !file) payload.remove_file = "1";

    setLoading(true);
    const url = editing
      ? `financial/declarations/${editing.id}/details.json`
      : "financial/declarations/submit.json";
    const data = await api.post_form(url, payload);
    setLoading(false);

    if (data?.id) {
      message.success($t("Declaratie opgeslagen"));
      onSaved(data);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={editing ? $t("Declaratie wijzigen") : $t("Declaratie indienen")}
      okText={$t("Opslaan")}
      cancelText={$t("Annuleren")}
      onOk={save}
      confirmLoading={loading}
    >
      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 16 }}
        message={$t(
          "Een beheerder beoordeelt je declaratie. Pas na goedkeuring wordt het bedrag bij je saldo bijgeschreven.",
        )}
      />
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="product_id"
          label={$t("Categorie")}
          rules={[{ required: true, message: $t("Kies een categorie") }]}
        >
          <Select
            options={products.map((p) => ({ value: p.id, label: p.name }))}
          />
        </Form.Item>
        {product?.description && (
          <p className="small gray" style={{ marginTop: -12, marginBottom: 16 }}>
            {product.description}
          </p>
        )}
        <Form.Item
          name="amount"
          label={$t("Bedrag")}
          rules={[
            { required: true, message: $t("Vul een bedrag in") },
            {
              validator: (_, value) =>
                !value || Number(value) > 0
                  ? Promise.resolve()
                  : Promise.reject($t("Bedrag moet groter zijn dan 0")),
            },
          ]}
        >
          <MoneyInput step={0.01} min={0} />
        </Form.Item>
        <Form.Item name="remark" label={$t("Opmerking")}>
          <Input.TextArea rows={3} maxLength={2000} />
        </Form.Item>
        <Form.Item
          label={
            product?.declaration_attachment_mandatory
              ? $t("Bijlage (verplicht)")
              : $t("Bijlage")
          }
          extra={$t("Bijvoorbeeld een foto of PDF van de bon.")}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {hasExistingFile && !file && <Tag>{existingFilename}</Tag>}
            {file && <Tag color="blue">{file.name}</Tag>}
            <Upload
              beforeUpload={selectFile}
              showUploadList={false}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.txt,.csv,.xls,.xlsx,.doc,.docx"
            >
              <Button size="small" icon={<UploadOutlined />}>
                {file || hasExistingFile ? $t("Vervangen") : $t("Bijlage kiezen")}
              </Button>
            </Upload>
            {(file || hasExistingFile) && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setFile(null);
                  setRemoveFile(true);
                }}
              >
                {$t("Verwijder")}
              </Button>
            )}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
